import type { TFunction } from 'i18next'
import type { Product } from '../lib/types'

/** Product-level display constants — not account-specific billing config, so
 * these live as frontend constants rather than DB columns (each product
 * currently has exactly one package/tier — see the prototype's CFG.mcp.pkg /
 * CFG.agt.plans[0], which is the same shape).
 *
 * `wordSingularKey` is a translation-key suffix into `common.words` (looked
 * up with a template-literal key, so it bypasses the i18next.d.ts typecheck
 * the same way Studio's own dynamic keys do — see mambu-sparks-studio's
 * `i18n/DYNAMIC_KEYS.md`). `tierName` is left untranslated: product/tier
 * names are proper nouns (never translated, same rule as account/tenant
 * names).
 *
 * The package box's copy (note, entry fee, feature bullets, deployment) went
 * with the box itself — ARCHITECTURE_DECISIONS.md §56.
 */
export const PRODUCT_INFO: Record<
  Product,
  {
    tierName: string
    wordSingularKey: 'invocation' | 'credit'
  }
> = {
  'Mambu MCP': {
    tierName: 'Mambu MCP',
    wordSingularKey: 'invocation',
  },
  'Mambu Agents': {
    tierName: 'Analyse',
    wordSingularKey: 'credit',
  },
}

export function wordSingular(t: TFunction, product: Product): string {
  return t(`common.words.${PRODUCT_INFO[product].wordSingularKey}`)
}

/** Trims a stored per-unit rate (`Numeric(14,6)` — see models/subscription.py)
 * down to the fewest decimals that represent it exactly, never fewer than 2 —
 * matches the prototype's rate display (`€0.005`, `€5.00`), not the raw
 * six-decimal precision the DB actually stores (`€0.010000`). */
export function fmtRate(value: string | number): string {
  const num = Number(value)
  if (!Number.isFinite(num)) return String(value)
  const trimmed = num.toFixed(6).replace(/0+$/, '').replace(/\.$/, '')
  const decimals = trimmed.includes('.') ? trimmed.split('.')[1].length : 0
  return num.toFixed(Math.max(2, decimals))
}
